import axiosInstance from '../core/services/network.service';
import * as endpoints from '../constants/endpoints';
import TokenService from './token.service';
import { refreshToken } from '../actions/auth';

// Single request/response interceptor pair for network.service's shared
// axios instance. A 403 is treated as "access token expired": the first
// one to hit it refreshes the token, every other 403 that arrives while
// that refresh is in flight queues behind it instead of firing its own
// refresh call, and once refreshed the original request is retried and
// its result is what the caller actually gets back.
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

const setup = (store: any) => {
  axiosInstance.axiosInstance.interceptors.request.use(
    (config) => {
      const token = TokenService.getLocalAccessToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  const { dispatch } = store;
  axiosInstance.axiosInstance.interceptors.response.use(
    (res) => {
      return res;
    },
    async (err) => {
      const originalConfig = err.config;

      if (
        !originalConfig ||
        !err.response ||
        originalConfig.url === endpoints.REFRESH_TOKEN ||
        originalConfig.url === endpoints.LOGIN
      ) {
        return Promise.reject(err);
      }

      if (err.response.status !== 403 || originalConfig._retry) {
        return Promise.reject(err);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token) => {
            originalConfig._retry = true;
            originalConfig.headers.Authorization = `Bearer ${token}`;
            axiosInstance
              .axiosInstance(originalConfig)
              .then(resolve)
              .catch(reject);
          });
        });
      }

      originalConfig._retry = true;
      isRefreshing = true;

      try {
        const res = await axiosInstance.axiosInstance.post(
          endpoints.REFRESH_TOKEN,
          { refresh: TokenService.getLocalRefreshToken() }
        );
        const { access } = res.data;

        TokenService.updateLocalAccessToken(access);
        dispatch(refreshToken(access));
        isRefreshing = false;
        onTokenRefreshed(access);

        originalConfig.headers.Authorization = `Bearer ${access}`;
        return await axiosInstance.axiosInstance(originalConfig);
      } catch (refreshErr) {
        isRefreshing = false;
        refreshSubscribers = [];
        TokenService.removeUser();
        window.location.replace('/');
        return Promise.reject(refreshErr);
      }
    }
  );
};

export default setup;
